import { useEffect, useState } from "react";
import { Bookmark, BookmarkCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { DemoPreview } from "@/src/features/demo-contracts/types";

/* ─── State machine for saved view ─────────────────────────────────────── */

function useSavedViewState(preview: DemoPreview, savedPainPointIds: string[]) {
  const [viewMode, setViewMode] = useState<"all" | "saved">("all");
  const [showClearFeedback, setShowClearFeedback] = useState(false);
  const savedSet = new Set(savedPainPointIds);
  const savedCount = preview.painPoints.filter((pp) =>
    savedSet.has(pp.id),
  ).length;

  const visiblePainPoints =
    viewMode === "saved"
      ? preview.painPoints.filter((pp) => savedSet.has(pp.id))
      : preview.painPoints;

  const [prevSavedCount, setPrevSavedCount] = useState(savedCount);

  if (savedCount !== prevSavedCount) {
    setPrevSavedCount(savedCount);
    if (savedCount === 0 && viewMode === "saved") {
      setViewMode("all");
    }
  }

  useEffect(() => {
    if (!showClearFeedback) return;
    const id = window.setTimeout(() => setShowClearFeedback(false), 2800);
    return () => window.clearTimeout(id);
  }, [showClearFeedback]);

  function handleClearSaved() {
    if (savedCount === 0) return;
    setShowClearFeedback(true);
  }

  return {
    viewMode,
    setViewMode,
    visiblePainPoints,
    savedCount,
    showClearFeedback,
    handleClearSaved,
  };
}

/* ─── Pain point item ───────────────────────────────────────────────────── */

function PainPointItem({
  painPoint,
  index,
  isSaved,
  onToggleSaved,
}: {
  painPoint: DemoPreview["painPoints"][number];
  index: number;
  isSaved: boolean;
  onToggleSaved: () => void;
}) {
  return (
    <article className="rounded-xl border border-white/10 bg-black/20 px-4 py-4 sm:px-3 sm:py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {/* Rank + title */}
          <p className="font-mono text-[11px] text-primary-foreground/40">
            {String(index + 1).padStart(2, "0")}
          </p>
          <p className="mt-0.5 font-serif text-[15px] font-semibold text-primary-foreground/84 sm:text-[16px]">
            {painPoint.title}
          </p>
          <p className="mt-1 font-mono text-[12px] leading-[1.8] text-primary-foreground/72 sm:text-[13px]">
            {painPoint.description}
          </p>
          {/* Metadata row */}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="font-mono text-[11px] text-primary-foreground/40">
              Severity {painPoint.severityScore}%
            </span>
            <span className="font-mono text-[11px] text-primary-foreground/40">
              {painPoint.frequency} mentions
            </span>
            <Badge
              variant="outline"
              className="rounded-md border-white/10 bg-white/5 font-mono text-[11px] text-primary-foreground/60"
            >
              {painPoint.category.replace(/_/g, " ")}
            </Badge>
          </div>
          {/* Evidence citation trigger */}
          {painPoint.evidence.length > 0 && (
            <button
              type="button"
              onClick={() => {
                const el = document.getElementById(
                  `evidence-block-${painPoint.id}`,
                );
                if (el?.scrollIntoView) {
                  el.scrollIntoView({ behavior: "smooth", block: "nearest" });
                }
                el?.setAttribute("tabindex", "-1");
                el?.focus();
                el?.classList.add("ring-1", "ring-accent/40");
                window.setTimeout(() => {
                  el?.classList.remove("ring-1", "ring-accent/40");
                }, 1500);
              }}
              className="mt-2 font-mono text-[11px] text-accent/70 underline decoration-accent/20 underline-offset-2 transition-colors hover:text-accent"
              aria-label={`Scroll to ${painPoint.evidence.length} evidence quote${painPoint.evidence.length !== 1 ? "s" : ""} for ${painPoint.title}`}
            >
              Jump to {painPoint.evidence.length} citation
              {painPoint.evidence.length !== 1 ? "s" : ""}
            </button>
          )}
        </div>

        {/* Save toggle */}
        <button
          type="button"
          onClick={onToggleSaved}
          className={cn(
            "shrink-0 rounded-md border px-2 py-1 font-mono text-[10px] uppercase tracking-widest transition-colors",
            isSaved
              ? "border-accent/30 bg-accent/10 text-accent/80"
              : "border-white/15 bg-white/5 text-primary-foreground/45 hover:border-white/25",
          )}
          aria-pressed={isSaved}
        >
          {isSaved ? (
            <>
              <BookmarkCheck className="mb-0.5 size-3" aria-hidden="true" />
              Saved
            </>
          ) : (
            <>
              <Bookmark className="mb-0.5 size-3" aria-hidden="true" />
              Save
            </>
          )}
        </button>
      </div>

      {/* Evidence block */}
      {painPoint.evidence.length > 0 && (
        <details
          id={`evidence-block-${painPoint.id}`}
          className="mt-3 rounded-lg border border-white/10 bg-white/[0.03]"
          aria-label={`Citation evidence for ${painPoint.title}`}
        >
          <summary className="cursor-pointer list-none px-3 py-2.5 font-mono text-[11px] uppercase tracking-widest text-primary-foreground/62">
            Read supporting citations
          </summary>
          <div className="space-y-2 border-t border-white/10 px-3 py-3">
            {painPoint.evidence.map((ev, evidenceIndex) => (
              <blockquote
                key={`${painPoint.id}-${ev.id}-${evidenceIndex}`}
                className="rounded-lg border border-white/10 bg-black/15 px-3 py-2 last:mb-0"
              >
                <p className="font-mono text-[11px] italic leading-[1.8] text-primary-foreground/76 sm:text-[12px]">
                  &ldquo;{ev.text}&rdquo;
                </p>
                <p className="mt-1 font-mono text-[9px] text-primary-foreground/45">
                  {ev.author}
                  {ev.community ? ` — ${ev.community}` : ""}
                  {ev.sourceLabel ? ` (${ev.sourceLabel})` : ""}
                </p>
                {ev.permalink || ev.sourceUrl ? (
                  <a
                    href={ev.permalink ?? ev.sourceUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 inline-flex font-mono text-[9px] text-accent/70 underline decoration-accent/20 underline-offset-2 transition-colors hover:text-accent"
                  >
                    View citation →
                  </a>
                ) : null}
              </blockquote>
            ))}
          </div>
        </details>
      )}
    </article>
  );
}

/* ─── Main section ───────────────────────────────────────────────────────── */

export function PainPointsSection({
  preview,
  savedPainPointIds,
  onToggleSaved,
  onClearSaved,
}: {
  preview: DemoPreview;
  savedPainPointIds: string[];
  onToggleSaved: (painPointId: string) => void;
  onClearSaved: () => void;
}) {
  const {
    viewMode,
    setViewMode,
    visiblePainPoints,
    savedCount,
    showClearFeedback,
    handleClearSaved,
  } = useSavedViewState(preview, savedPainPointIds);

  const savedSet = new Set(savedPainPointIds);
  const hasVisibleEvidence = visiblePainPoints.some(
    (pp) => pp.evidence.length > 0,
  );

  return (
    <section className="space-y-4 rounded-xl border border-white/10 bg-white/[0.03] p-4 sm:space-y-3">
      {/* Header row */}
      <div className="flex items-center justify-between gap-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-primary-foreground/40">
          Pain signals ranked by urgency and frequency
        </p>
        <div className="flex items-center gap-2">
          {/* View mode toggle */}
          <div className="flex items-center rounded-md border border-white/15 bg-white/5 p-0.5">
            {(["all", "saved"] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                className={cn(
                  "rounded px-2 py-1 font-mono text-[10px] uppercase tracking-widest transition-colors",
                  viewMode === mode
                    ? "bg-white/15 text-primary-foreground"
                    : "text-primary-foreground/50",
                )}
                aria-pressed={viewMode === mode}
                onClick={() => setViewMode(mode)}
              >
                {mode === "all" ? "All" : "Saved"}
              </button>
            ))}
          </div>
          <Badge className="font-mono border-white/15 bg-white/5 text-[10px] text-primary-foreground/50">
            {savedCount} saved
          </Badge>
          <button
            type="button"
            className="font-mono text-[10px] text-primary-foreground/35 underline decoration-white/10 underline-offset-2 transition-colors hover:text-primary-foreground/55 disabled:cursor-not-allowed disabled:opacity-30"
            onClick={() => {
              handleClearSaved();
              onClearSaved();
            }}
            disabled={savedCount === 0}
          >
            Clear saved
          </button>
        </div>
      </div>
      <p className="font-mono text-[12px] leading-[1.7] text-primary-foreground/70 sm:text-[13px]">
        Start with the highest-friction problems, then open the citations to
        verify that the wording reflects real customer pain.
      </p>

      {/* Empty states */}
      {visiblePainPoints.length === 0 ? (
        <p className="rounded-lg border border-white/10 bg-black/20 p-3 font-mono text-[11px] text-primary-foreground/58">
          {viewMode === "saved"
            ? "You have not saved any pain signals yet. Save one to create a shorter review list."
            : "This preview does not include pain signals yet."}
        </p>
      ) : !hasVisibleEvidence ? (
        <p className="rounded-lg border border-white/10 bg-black/20 p-3 font-mono text-[11px] text-primary-foreground/58">
          Citation support is not available in this preview yet.
        </p>
      ) : null}

      {/* Pain point list */}
      <div className="space-y-2">
        {visiblePainPoints.map((painPoint, index) => (
          <PainPointItem
            key={painPoint.id}
            painPoint={painPoint}
            index={index}
            isSaved={savedSet.has(painPoint.id)}
            onToggleSaved={() => onToggleSaved(painPoint.id)}
          />
        ))}
      </div>

      {/* Clear feedback */}
      {showClearFeedback ? (
        <p
          className="font-mono text-[11px] text-primary-foreground/40"
          role="status"
        >
          Saved pain signals cleared.
        </p>
      ) : null}
    </section>
  );
}
